import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, UserCircleCheck, UserCircleMinus, LockKey, Trash } from "@phosphor-icons/react";

/**
 * v1.27.90 — Manage Agents (sub-users) for one party.
 * Used both by:
 *   • Admin → Administration → Parties row menu → "Manage Agents"
 *   • Portal → Settings → "Manage my Agents"
 *
 * Backend endpoints are the same in both contexts — `_require_agent_manager`
 * on the server side gates by role + party ownership.
 */
export default function ManageAgentsDialog({ open, onOpenChange, party, endpoint }) {
    const partyId = party?.id;
    const partyName = party?.name || "Party";
    const base = endpoint || (partyId ? `/parties/${partyId}/agents` : "");
    const [agents, setAgents] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        if (!open || !base) return;
        try {
            const { data } = await api.get(base);
            setAgents(Array.isArray(data) ? data : (data.agents || []));
        } catch (e) { toast.error(formatApiError(e)); }
    }, [open, base]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (!open) { setForm({ name: "", email: "", phone: "", password: "" }); setEditingId(null); } }, [open]);

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Agent name required");
        if (!form.email.trim() && !form.phone.trim()) return toast.error("Email or mobile is required");
        if (!editingId && (form.password || "").length < 6) return toast.error("Password must be at least 6 characters");
        setBusy(true);
        try {
            if (editingId) {
                const patch = { name: form.name, email: form.email, phone: form.phone };
                if (form.password) patch.password = form.password;
                await api.patch(`/parties/${partyId}/agents/${editingId}`, patch);
                toast.success("Agent updated");
            } else {
                await api.post(`/parties/${partyId}/agents`, form);
                toast.success(`Agent "${form.name}" added`);
            }
            setForm({ name: "", email: "", phone: "", password: "" });
            setEditingId(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const toggleActive = async (a) => {
        try {
            await api.post(`/parties/${partyId}/agents/${a.id}/${a.active === false ? "activate" : "deactivate"}`);
            toast.success(`Agent ${a.active === false ? "reactivated" : "deactivated"}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const startEdit = (a) => {
        setEditingId(a.id);
        setForm({ name: a.name || "", email: a.email || "", phone: a.phone || "", password: "" });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="manage-agents-dialog" className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlus size={18} weight="fill" className="text-indigo-600" /> Manage Agents · {partyName}
                    </DialogTitle>
                    <DialogDescription>
                        Multiple agents can log in under this party. Each agent's uploads land in the admin's Pending queue tagged with their name. Agents see only their own uploads; the party account holder sees all.
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        {editingId ? "Edit Agent" : "Add New Agent"}
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Name *</Label>
                            <Input data-testid="agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Password {editingId ? "(blank keeps existing)" : "*"}</Label>
                            <Input data-testid="agent-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 chars" className="mt-1 bg-white" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Email</Label>
                            <Input data-testid="agent-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="agent@example.com" className="mt-1 bg-white" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Mobile (10 digits)</Label>
                            <Input data-testid="agent-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="9876543210" className="mt-1 bg-white" inputMode="tel" />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        {editingId && (
                            <Button variant="outline" size="sm" onClick={() => { setEditingId(null); setForm({ name: "", email: "", phone: "", password: "" }); }}>Cancel edit</Button>
                        )}
                        <Button data-testid="save-agent-btn" size="sm" onClick={submit} disabled={busy} className="bg-indigo-600 text-white hover:bg-indigo-700">
                            {busy ? "Saving…" : (editingId ? "Update agent" : "Add agent")}
                        </Button>
                    </div>
                </div>

                <div className="mt-2">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-600">{agents.length} agent{agents.length === 1 ? "" : "s"}</div>
                    {agents.length === 0 && <div className="rounded border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">No agents yet. Add one above.</div>}
                    <ul className="divide-y divide-slate-100 rounded border border-slate-200">
                        {agents.map((a) => (
                            <li key={a.id} data-testid={`agent-row-${a.id}`} className="flex flex-wrap items-center justify-between gap-2 p-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-900">{a.name}</span>
                                        {a.active === false && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-rose-700">Deactivated</span>}
                                    </div>
                                    <div className="text-xs text-slate-500">{a.email || "—"} · {a.phone || "—"}</div>
                                </div>
                                <div className="flex gap-1.5">
                                    <Button size="sm" variant="outline" onClick={() => startEdit(a)} data-testid={`edit-agent-${a.id}`}>
                                        <LockKey size={12} className="mr-1" /> Reset / Edit
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => toggleActive(a)} data-testid={`toggle-agent-${a.id}`}
                                        className={a.active === false ? "border-emerald-300 text-emerald-700" : "border-rose-300 text-rose-700"}>
                                        {a.active === false ? <><UserCircleCheck size={12} className="mr-1" /> Activate</> : <><UserCircleMinus size={12} className="mr-1" /> Deactivate</>}
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
